<template>
  <div class="px-4 pb-4 pt-2">
    <!-- Loading state -->
    <template v-if="loading">
      <v-row align="center" justify="center">
        <v-col cols="12" class="text-center">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          <div class="mt-4">{{ $t("common.action.loading") }}</div>
        </v-col>
      </v-row>
    </template>

    <!-- Main content -->
    <template v-else>
      <!-- Action buttons -->
      <v-row class="" v-if="allLocations?.length">
        <v-col cols="12" class="d-flex align-center">
          <v-spacer></v-spacer>
          <v-btn color="primary" prepend-icon="mdi-plus" @click="createLocation()">
            {{ $t("common.action.addLocation") }}
          </v-btn>
        </v-col>
      </v-row>

      <!-- Empty state -->
      <v-card v-if="!allLocations?.length">
        <empty-card
          :mainText="$t('page.locations.noLocation')"
          :buttonText="$t('common.action.addLocation')"
          :buttonColor="'primary'"
          @button-click="createLocation"
        >
          <template #image>
            <v-img src="@/shared/assets/map.png" max-height="150px" min-height="75px" />
          </template>
        </empty-card>
      </v-card>

      <!-- Locations table -->
      <v-card v-else class="mt-6">
        <v-data-table
          :headers="headers"
          :items="locations"
          hide-default-footer
          :loading="loading"
          loading-text="Loading locations..."
          hover
          @click:row="goReaderDetail"
        >
          <template v-slot:item="{ item }">
            <tr @click="goReaderDetail($event, { item })" :key="item.id" style="cursor: pointer">
              <!-- Icon -->
              <td class="text-center">
                <v-icon color="#E0E0E0" size="32">mdi-map-marker</v-icon>
              </td>

              <!-- Name -->
              <td class="text-start">
                <div class="text-subtitle-1">
                  {{ item.display_name }}
                </div>
              </td>

              <!-- Terminals -->
              <td class="text-center">
                <span v-if="item.terminalCounts" class="text-body-2">
                  {{ item.terminalCounts.active }} /
                  {{ item.terminalCounts.total }}
                </span>
                <span v-else class="text-grey">-</span>
              </td>

              <!-- Address -->
              <td class="text-start">
                <div v-if="item.address">
                  <div class="text-body-2">
                    {{ item.address.line1 }}
                  </div>
                  <div class="text-body-2 text-grey">
                    {{ item.address.city
                    }}<span v-if="item.address.state">, {{ item.address.state }}</span
                    ><span v-if="item.address.postal_code"
                      >&nbsp;{{ item.address.postal_code }}</span
                    >
                  </div>
                </div>
                <span v-else>-</span>
              </td>

              <!-- Actions -->
              <td class="text-center">
                <v-menu>
                  <template v-slot:activator="{ props }">
                    <v-btn v-bind="props" icon flat @click.stop>
                      <v-icon color="grey">mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="(action, index) in actionList"
                      :key="index"
                      :value="index"
                      @click.stop="action.action(item.id)"
                    >
                      <v-list-item-title>{{ action.title }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </v-data-table>

        <!-- Pagination controls -->
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            @click="prevPage"
            :disabled="currentPage === 1 || loading"
            :loading="loading"
          >
            Prev
          </v-btn>
          <v-btn
            color="primary"
            @click="nextPage"
            :disabled="!hasMore || loading"
            :loading="loading"
          >
            Next
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </div>
  <location-dialog
    ref="locationDialog"
    :dialogAction="dialogAction"
    @updated="handleTerminalUpdated()"
  />
  <base-confirm-dialog
    v-model="dialogVisible"
    @close="dialogVisible = false"
    :title="$t('common.action.deleteLocation')"
    :actionName="$t('common.action.delete')"
    :actionColor="'red'"
    @action="deleteLocation()"
    :max-width="$vuetify.display.mdAndUp ? '30%' : '100%'"
    :showCancel="$t('common.action.cancel')"
    :isLoading="buttonLoading"
  >
    <template #content>
      {{ $t("page.locations.deleteLocationConfirm") }}
    </template>
  </base-confirm-dialog>
</template>

<script lang="ts" setup>
import { useLocationStore } from "@/terminals/stores/locations";
import { ref, computed, onMounted } from "vue";
import LocationDialog from "@/terminals/components/location-and-terminals/LocationDialog.vue";
import BaseConfirmDialog from "@/shared/components/Common/BaseConfirmDialog.vue";
import i18n from "@/app/plugins/i18n";
import { useRouter } from "vue-router";
import EmptyCard from "@/shared/components/Common/EmptyCard.vue";
import type { LocationSummary } from "@/terminals/types/locations";

interface LocationTableHeader {
  title: string;
  align: "start" | "center" | "end";
  key: string;
  sortable: boolean;
  width?: string;
}

const locationStore = useLocationStore();
const locationDialog = ref<null | { open: (id?: string) => null }>(null);
const buttonLoading = ref(false);
const allLocations = computed(() => locationStore.paginatedLocations.locations);
const locations = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return allLocations.value.slice(start, end);
});
const dialogAction = ref("");
const dialogVisible = ref(false);
const router = useRouter();
const selectedLocationId = ref();
const loading = computed(() => locationStore.paginatedLoading);

// Pagination state
const currentPage = ref(1);
const itemsPerPage = 10;
const hasMore = computed(() => {
  // This would normally come from the API, but for now we'll calculate it
  return allLocations.value.length > itemsPerPage * currentPage.value;
});

onMounted(async () => {
  await loadLocations();
});

const loadLocations = async () => {
  await locationStore.listLocations(false, true, "paginated");
};

const handleTerminalUpdated = async () => {
  await loadLocations();
};

// Simplified headers without "Location" prefix
const headers: LocationTableHeader[] = [
  {
    title: "",
    align: "center",
    key: "icon",
    sortable: false,
    width: "60px",
  },
  {
    title: i18n.global.t("common.action.name"),
    align: "start",
    key: "name",
    sortable: true,
  },
  {
    title: i18n.global.t("page.locations.terminals"),
    align: "center",
    key: "terminals",
    sortable: false,
  },
  {
    title: i18n.global.t("page.locations.address"),
    align: "start",
    key: "address",
    sortable: true,
  },
  {
    title: "",
    align: "center",
    key: "menu",
    sortable: false,
    width: "60px",
  },
];

const actionList = [
  {
    title: i18n.global.t("common.action.editLocation"),
    action: (id: string) => editLocation(id),
  },
  {
    title: i18n.global.t("common.action.deleteLocation"),
    action: (id: string) => openDeleteLocationDialog(id),
  },
];

const createLocation = async () => {
  dialogAction.value = "create";
  locationDialog.value?.open();
};

const editLocation = (id: string) => {
  dialogAction.value = "edit";
  locationDialog.value?.open(id);
};

const deleteLocation = async () => {
  buttonLoading.value = true;
  await locationStore.deleteLocation(selectedLocationId.value);
  await loadLocations();
  dialogVisible.value = false;
  buttonLoading.value = false;
};

const goReaderDetail = (event: Event, context: { item: LocationSummary }) => {
  router.push({ path: `/locations/${context.item.id}` });
};

const openDeleteLocationDialog = (id: string) => {
  dialogVisible.value = true;
  selectedLocationId.value = id;
};

// Pagination methods
const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    // In the future, this would trigger a new API call with pagination params
  }
};

const nextPage = () => {
  if (hasMore.value) {
    currentPage.value++;
    // In the future, this would trigger a new API call with pagination params
  }
};
</script>
